import React from 'react';

import { IconButton } from '@material-ui/core';
import { FiChevronDown } from 'react-icons/fi';
import clsx from 'clsx';

import { CompanyRoleDTO } from '@vyce/core/src/api/types';
import { requiresResourceSelection } from '@vyce/core/src/utils/staffPermissions';

import { ModuleDraft, ModulePermissionState, VisibleStaffModule } from '../types';
import { getModuleIcon } from '../utils/staffModules';
import { CatalogueIndex } from '../utils/permissionState';
import { getRoleTier, useRoleChipStyles } from '../utils/roleChip';
import { useRolesStyles } from '../styles';
import { RoleChipDropdown } from './RoleChipDropdown';
import { ModulePermissionPanel } from './ModulePermissionPanel';
import { scopeSummary } from './ScopeSelector';

const FULL_ACCESS_LABEL = 'Full access';

interface Props {
  companyId?: string;
  userId: string;
  module: VisibleStaffModule;
  draft: ModuleDraft;
  permissionState: ModulePermissionState;
  effective: Set<string>;
  catalogue: CatalogueIndex | null;
  disabled?: boolean;
  isOwner?: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onRoleChange: (role: CompanyRoleDTO | null) => void;
  onResourceToggle: (id: string, checked: boolean) => void;
  onSelectAllResources: (checked: boolean) => void;
  onPermissionToggle: (systemName: string, checked: boolean) => void;
  onResetPermissions: () => void;
  onClearResourceOverridesChange: (value: boolean) => void;
  onExceptionsLoaded?: (hasExceptions: boolean) => void;
}

export const ModuleRoleRow: React.FC<Props> = ({
  companyId,
  userId,
  module,
  draft,
  permissionState,
  effective,
  catalogue,
  disabled,
  isOwner,
  expanded,
  onToggleExpand,
  onRoleChange,
  onResourceToggle,
  onSelectAllResources,
  onPermissionToggle,
  onResetPermissions,
  onClearResourceOverridesChange,
  onExceptionsLoaded,
}) => {
  const classes = useRolesStyles();
  const chipClasses = useRoleChipStyles();
  const Icon = getModuleIcon(module.systemName);
  // Modules like General and Billing carry permissions but have no assignable
  // role, so a role dropdown there would be an empty menu.
  const hasAssignableRoles = module.roles.length > 0;
  // The scope can't be read here, so a scoped role would be saved against a list
  // the viewer never saw — offer only the roles that need no resource.
  const roleOptions = module.inventoryUnavailable
    ? module.roles.filter(role => !requiresResourceSelection(role))
    : module.roles;
  // Permissions are editable for any assigned role now, not just resource-scoped
  // ones, so the panel opens whenever the person holds something in this module.
  const hasDetail = hasAssignableRoles
    ? !!draft.role ||
      permissionState.hasOverridesRow ||
      ((module.emptyInventory || module.inventoryUnavailable) && !!module.permissions.length)
    : module.permissions.length > 0;
  const showScopeSummary =
    !!draft.role &&
    requiresResourceSelection(draft.role) &&
    module.resources.length > 0 &&
    !!module.resourceLabel;

  // Count against the module's own catalogue: implied_permissions can cross
  // module boundaries, so the closure may hold names that don't belong here.
  const grantedCount = isOwner
    ? module.permissions.length
    : module.permissions.filter(permission => effective.has(permission.system_name)).length;
  const permissionCount = `${grantedCount} of ${module.permissions.length} ${
    module.permissions.length === 1 ? 'permission' : 'permissions'
  }`;

  return (
    <div className={classes.moduleRow}>
      <div className={classes.moduleRowMain}>
        <span className={classes.moduleIcon}>
          <Icon size={18} />
        </span>
        <div className={classes.moduleLabel}>
          {module.label}
          {showScopeSummary && (
            <div className={classes.moduleSubtitle}>
              {scopeSummary(
                module.resourceLabel as string,
                draft.resourceIds.length,
                module.resources.length
              )}
            </div>
          )}
          {module.emptyInventory && (
            <div className={classes.moduleSubtitle}>
              No {(module.resourceLabel || 'resources').toLowerCase()} yet — set the scope once the
              first one is created
            </div>
          )}
          {module.inventoryUnavailable && (
            <div className={classes.moduleSubtitle}>
              You can't view this company's {(module.resourceLabel || 'resources').toLowerCase()}, so the
              scope can't be changed here
            </div>
          )}
        </div>
        {/* owner: true short-circuits every check on the backend, so a role chip
            here would understate what this person can actually do. */}
        {isOwner ? (
          <span className={clsx(chipClasses.chip, chipClasses[getRoleTier(FULL_ACCESS_LABEL)])}>
            {FULL_ACCESS_LABEL}
          </span>
        ) : hasAssignableRoles ? (
          <RoleChipDropdown
            moduleSystemName={module.systemName}
            value={draft.role}
            unresolvedRoleName={draft.unresolvedRoleName}
            options={roleOptions}
            // Every role here needs a scope this viewer can't see — leave the chip
            // as a read-out rather than offering a pick that would blank it.
            disabled={disabled || (module.inventoryUnavailable && !roleOptions.length && !draft.role)}
            onChange={onRoleChange}
          />
        ) : (
          <span className={classes.permissionCount}>{permissionCount}</span>
        )}
        {hasDetail && (
          <IconButton size="small" className={classes.detailToggle} onClick={onToggleExpand}>
            <span className={clsx(classes.chevron, expanded && classes.chevronOpen)}>
              <FiChevronDown size={15} />
            </span>
          </IconButton>
        )}
      </div>
      {hasDetail && (
        <ModulePermissionPanel
          companyId={companyId}
          userId={userId}
          module={module}
          draft={draft}
          permissionState={permissionState}
          effective={effective}
          catalogue={catalogue}
          // An empty inventory no longer locks the panel — permissions can be set
          // before the company has its first team. Only an unreadable one does.
          disabled={disabled || module.inventoryUnavailable}
          isOwner={isOwner}
          open={expanded}
          onResourceToggle={onResourceToggle}
          onSelectAllResources={onSelectAllResources}
          onPermissionToggle={onPermissionToggle}
          onReset={onResetPermissions}
          onClearResourceOverridesChange={onClearResourceOverridesChange}
          onExceptionsLoaded={onExceptionsLoaded}
        />
      )}
    </div>
  );
};
